import { canSelectRevisionTemplate } from 'utils/revision-workflow';
import { LoadingOverlay } from '@mantine/core';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import TemplateSelect from 'pages/revisions-module/select-template-and-visual/TemplateSelect';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  DASHBOARD_PAGE_PATH,
  FILL_OUT_FORM_PAGE_PATH,
  FILL_OUT_REVISION_PAGE_PATH,
  FORM_PAGE_PATH,
  FORMS_PAGE_PATH,
  REVISION_PLAN_ASSIGNMENTS_PAGE_PATH,
  REVISION_PAGE_PATH,
  REVISIONS_PAGE_PATH,
} from 'routes/paths';
import { NO_ENTITY_EXISTS_ERROR_CODE } from 'utils/constants';

/**
 * Page used to select a render-ready revision template.
 */
export default function SelectTemplatePage() {
  const navigate = useNavigate();
  const { revisionId } = useParams();
  const { getAction, userId } = useApi();
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState<RevisionGetResponse | null>(null);

  const fillOutRevisionPath = useMemo(
    () =>
      revision?.kind === 'revision'
        ? FILL_OUT_REVISION_PAGE_PATH.insert({ revisionId })
        : FILL_OUT_FORM_PAGE_PATH.insert({ revisionId }),
    [revision?.kind, revisionId]
  );
  const revisionDetailPath = useMemo(
    () =>
      revision?.kind === 'revision' ? REVISION_PAGE_PATH.insert({ revisionId }) : FORM_PAGE_PATH.insert({ revisionId }),
    [revision?.kind, revisionId]
  );

  useEffect(() => {
    if (revisionId) {
      const getRevision = getAction('RevisionGet');
      const getPlanAssignments = getAction('RevisionGetPlanAssignments');

      getRevision({ parameters: { revisionId } })
        .then(async (response) => {
          if (
            response.kind === 'revision' &&
            canSelectRevisionTemplate(response) &&
            response.technician?.userId === userId
          ) {
            const assignmentState = await getPlanAssignments({ parameters: { revisionId } });

            if (!assignmentState.allDevicesDecided) {
              navigate(REVISION_PLAN_ASSIGNMENTS_PAGE_PATH.insert({ revisionId }), { replace: true });
              return;
            }
          }

          setRevision(response);
        })
        .catch((error) => {
          if (error?.response?.data?.error?.code === NO_ENTITY_EXISTS_ERROR_CODE) {
            navigate(DASHBOARD_PAGE_PATH.original, { replace: true });
          } else {
            panic(error);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [getAction, navigate, revisionId, userId]);

  /** Persists the selected render-ready template and starts the revision or form. */
  function handleTemplateChange({ template }: { template: string }) {
    const action = getAction('RevisionSetTemplate');

    setLoading(true);

    const templateModified = !revision!.revisionTemplate?.slug || revision!.revisionTemplate.slug !== template;

    action({
      parameters: { revisionId: String(revisionId) },
      payload: {
        revisionData: templateModified ? JSON.stringify(null) : undefined,
        revisionTemplate: template,
      },
    })
      .then(() => navigate(fillOutRevisionPath))
      .catch(panic)
      .finally(() => setLoading(false));
  }

  if (
    revision &&
    (!revision.technician || revision.technician.userId !== userId || !canSelectRevisionTemplate(revision))
  ) {
    return <Navigate to={revisionDetailPath} replace />;
  }

  const selectionTitle =
    revision?.kind === 'revision' ? 'Vyberte šablónu revíznej správy' : 'Vyberte šablónu formulára';

  return (
    <DashboardLayout
      title={selectionTitle}
      breadcrumbs={[
        {
          title: revision?.kind === 'revision' ? 'Revízne správy' : 'Formuláre',
          link: revision?.kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original,
        },
        { title: selectionTitle },
      ]}
    >
      <LoadingOverlay visible={loading} />
      {revision && (
        <TemplateSelect
          kind={revision.kind}
          revisionId={revision.revisionId}
          loading={loading}
          onChange={handleTemplateChange}
          initialTemplateSlug={revision.revisionTemplate?.slug}
        />
      )}
    </DashboardLayout>
  );
}
